import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PhoneSettingsHelp } from '@/components/PhoneSettingsHelp';
import { ComputerAlertsHelp } from '@/components/AlertCheck';
import { BackIcon, SparkIcon } from '@/components/Icons';
import { useGoBack } from '@/lib/goBack';
import { STAFF_ASSISTANT_NAME } from '@/lib/assistant';

// The CMB Chats guide (Gary, 19 Oct): one page to send before login, written for the least technical user, as a help
// document rather than a brochure. Overview and quick start first, then the rules, Leo, features and set-up.
// How Leo works (/guide/leo) and the full set-up (/guide/setup) are their own pages. Opens for anyone, signed in or
// not. Every claim was checked against the code on 19 Oct: keep it true to the app and update it when a rule or a
// button changes. Screenshots are the app's sample data at phone width (public/guide).

const BASE = import.meta.env.BASE_URL;
const img = (name: string) => `${BASE}guide/${name}.jpg`;

/** This country's own address, never a hardcoded one. */
function useAddress(): string {
  // Opened inside Sentinel (za.app...), the guide still names the stand-alone Chats address (za.chats...), the one
  // that installs on a phone (Gary, 19 Oct: Debbie set up Sentinel's page and looped).
  return window.location.host.replace(/^([a-z]{2})\.app\./, '$1.chats.');
}

// Opened from inside the app (Help, set-up), each guide page has the app's back arrow at the top, back to where it
// was opened: a home-screen iPhone app has no other way back. Kept for the rest of the visit, as a tap on an
// in-page link drops the router state. The public link view is unchanged (mobile review, 19 Oct).
let openedFromApp = false;
function useFromApp(): boolean {
  const state = useLocation().state as { fromApp?: boolean } | null;
  if (state?.fromApp) openedFromApp = true;
  return openedFromApp;
}

/** A link to another guide page that keeps the in-app back arrow. */
function GuideLink({ to, className, children }: { to: string; className: string; children: ReactNode }) {
  const fromApp = useFromApp();
  return <Link className={className} to={to} state={fromApp ? { fromApp: true } : undefined}>{children}</Link>;
}

function Hero({ title, intro, mark, back, children }: { title: string; intro: string; mark: string; back?: boolean; children?: ReactNode }) {
  return (
    <header className={`chats-gd-hero${children ? '' : ' chats-gd-hero--sub'}`}>
      <div className="chats-gd-wrap">
        <div>
          {back && <GuideLink className="chats-gd-back" to="/guide">← CMB Chats guide</GuideLink>}
          <div className="chats-gd-mark"><span><SparkIcon /></span>{mark}</div>
          <h1>{title}</h1>
          <p>{intro}</p>
          {children && <div className="chats-gd-btns">{children}</div>}
        </div>
        {children && <div className="chats-gd-phone"><img src={img('chats')} alt="The CMB Chats chat list" width={390} height={844} /></div>}
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="chats-gd-footer">
      <div className="chats-gd-wrap">
        <p>Need help? Message head office on the Team tab, or contact us the way you normally do.</p>
        <p className="chats-gd-credit">Developed by <a href="https://www.praxa.co.za/" target="_blank" rel="noopener noreferrer">Praxa</a></p>
      </div>
    </footer>
  );
}

function Page({ title, children }: { title: string; children: ReactNode }) {
  const fromApp = useFromApp();
  const goBack = useGoBack();
  return (
    <div className="chats-app chats-guide-app">
      {fromApp && (
        <header className="chats-topbar chats-topbar--conv">
          <button type="button" className="chats-icon-btn chats-back" aria-label="Back" onClick={() => goBack('/help')}><BackIcon /></button>
          <h1 className="chats-topbar-title">{title}</h1>
        </header>
      )}
      <main className="chats-screen chats-gd">{children}<Footer /></main>
    </div>
  );
}

function Section({ kicker, title, lead, alt, leo, id, children }: { kicker: string; title: string; lead?: string; alt?: boolean; leo?: boolean; id?: string; children: ReactNode }) {
  return (
    <section id={id} className={`chats-gd-sec${alt ? ' chats-gd-sec--alt' : ''}${leo ? ' chats-gd-sec--leo' : ''}`}>
      <div className="chats-gd-wrap">
        <div className="chats-gd-kicker">{kicker}</div>
        <h2>{title}</h2>
        {lead && <p className="chats-gd-lead">{lead}</p>}
        {children}
      </div>
    </section>
  );
}

function Card({ icon, tone, title, children }: { icon?: string; tone?: 'green' | 'leo' | 'amber'; title: string; children: ReactNode }) {
  return (
    <div className="chats-gd-card">
      {icon && <div className={`chats-gd-icon${tone ? ` chats-gd-icon--${tone}` : ''}`} aria-hidden="true">{icon}</div>}
      <h3>{title}</h3>
      {children}
    </div>
  );
}

function Shot({ name, alt, caption }: { name: string; alt: string; caption: string }) {
  return (
    <figure className="chats-gd-shot">
      <img src={img(name)} alt={alt} width={390} height={844} loading="lazy" />
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

/** A row of tabs (iPhone, Android, Computer) with one panel showing. */
function Tabs({ tabs }: { tabs: { key: string; label: string; body: ReactNode }[] }) {
  const [on, setOn] = useState(tabs[0]?.key ?? '');
  return (
    <>
      <div className="chats-gd-tabs" role="tablist">
        {tabs.map((t) => (
          <button key={t.key} type="button" role="tab" className="chats-gd-tab" aria-selected={on === t.key} onClick={() => setOn(t.key)}>{t.label}</button>
        ))}
      </div>
      {tabs.map((t) => <div key={t.key} role="tabpanel" hidden={on !== t.key}>{t.body}</div>)}
    </>
  );
}

function More({ title, text, to, label }: { title: string; text: string; to: string; label: string }) {
  return (
    <div className="chats-gd-more">
      <p><b>{title}</b>{text}</p>
      <GuideLink className="chats-gd-btn chats-gd-btn--line" to={to}>{label}</GuideLink>
    </div>
  );
}

const FEATURES: { icon: string; tone?: 'green' | 'leo' | 'amber'; title: string; sub: string; items: ReactNode[] }[] = [
  { icon: '💬', tone: 'green', title: 'Chat list', sub: 'Search, filters, archive', items: [
    <><b>Search</b> finds names, numbers and words in messages.</>,
    <>Every open chat is in <b>one list</b>, newest first. Each chat shows its own unread count.</>,
    <><b>Filter</b> has No owner, status (Hot, Warm, Cold, New and the loyalty levels), area and labels.</>,
    <><b>Owner first</b> on every chat (You, a name, or No owner), then <b>Leo</b> in violet when he is on duty. His chats are shaded violet.</>,
    <><b>Then</b> Email, labels, and on the right Unpaid, the loyalty level, or for an enquiry Leo's grade: <b>Hot</b>, <b>Warm</b> or <b>Cold</b> (New until he has graded it).</>,
    <><b>The round + button</b> starts a new chat with an approved message.</>,
    <><b>Swipe left</b> to archive. This closes the chat; a new message from the customer starts a fresh one. <b>Unarchive</b> reopens it.</>,
    <>A chat quiet for 7 days with nothing owing archives itself.</>,
  ] },
  { icon: '✍️', title: 'In a chat', sub: 'Replies, notes, voice, links', items: [
    <><b>The top line</b> shows the owner and who is on duty, with a <b>switch</b>: flip it to take a chat from Leo or hand it back.</>,
    <><b>+</b> sends a photo or PDF, a team note, an address request or a quick link, or asks Leo to <b>Suggest a reply</b> for you to check and send.</>,
    <><b>Team notes</b> show in yellow. The customer does not see them. Type <b>@</b> and a name to alert that person.</>,
    <><b>Voice notes</b>: hold the microphone and speak.</>,
    <><b>Hold a message</b> to reply, star, react or forward. Or swipe it right to reply.</>,
  ] },
  { icon: '✉️', title: 'Email chats', sub: 'Replies to our emails', items: [
    <>When a customer replies to one of our emails, it shows here with an <b>Email</b> tag and an envelope on their picture.</>,
    <><b>Only emails sent or received through Chats show.</b> Emails from anyone's own mailbox never appear.</>,
    <>A customer on both? The chat has <b>WhatsApp</b> and <b>Email</b> tabs.</>,
    <>Type in <b>Write an email reply</b>. It goes out from CleanMyBed (as the area's name where it has one), and the owner gets a copy of each customer reply by email and can answer from their inbox. Words only: no photos, documents, voice notes or reactions.</>,
    <>No 24-hour rule on email, and no Leo duty switch.</>,
    <><b>Leo's draft</b> in the email bar opens a reply Leo suggests: check it, edit it, put it in the box and send. Leo never sends an email himself.</>,
    <><b>Head office:</b> emails to info@cleanmybed.com land here too. Leo closes junk (sales pitches, newsletters, spam) with a note and says whether the rest is a customer or not. Replies go as CleanMyBed from info@.</>,
  ] },
  { icon: '📋', tone: 'amber', title: 'Info', sub: 'Customer, Booking, History', items: [
    <><b>Customer</b>: contact details, address, notes and services so far. Call, email or open the map.</>,
    <><b>Booking</b>: the next booking, to edit, move or cancel, or New booking. A cancelled booking shows Book again.</>,
    <><b>History</b>: past services, invoices and what is owed.</>,
    <><b>Ask</b>: questions to {STAFF_ASSISTANT_NAME} about this customer.</>,
    <><b>Emails</b>: every conversation with this customer, email and WhatsApp.</>,
    <><b>Search this chat</b>, labels, starred messages and alerts for this chat.</>,
    <><b>Mark booked, Close chat, Junk</b> to finish a chat. Junk works when the only booking is an unconfirmed website booking, and cancels it; a confirmed booking stops it. A junked chat stays closed for 7 days: new messages are kept, but nobody is alerted and Leo does not reply.</>,
  ] },
  { icon: '✦', tone: 'leo', title: 'Ask', sub: `Private questions to ${STAFF_ASSISTANT_NAME}`, items: [
    <><b>Ask</b> {STAFF_ASSISTANT_NAME}, the team's assistant, a question. Leo is for customers, {STAFF_ASSISTANT_NAME} is for you. The customer does not see it.</>,
    <><b>Starred</b> and <b>Projects</b> keep useful answers together.</>,
  ] },
  { icon: '👥', tone: 'green', title: 'Team', sub: 'Colleagues, groups, channels', items: [
    <><b>Message</b> anyone on the team. These never go to WhatsApp.</>,
    <><b>Groups</b>: anyone can start one.</>,
    <><b>Channels</b>: announcements from head office.</>,
    <><b>Edit or delete</b> a team message within 15 minutes.</>,
  ] },
  { icon: '⚙️', title: 'You', sub: 'Settings, alerts, Help', items: [
    <><b>My availability</b>: the Leo on duty switch.</>,
    <><b>Notifications</b>: Send me a test notification.</>,
    <><b>Display</b>: list size, and light or dark.</>,
    <><b>Message sound</b>, <b>Undo before sending</b> and <b>Keep chats archived</b> switches.</>,
    <><b>Find a suburb</b> shows which area covers it.</>,
    <><b>Help</b> and <b>Training</b>.</>,
  ] },
];

function QuickSetup({ address }: { address: string }) {
  const leads = <div className="chats-gd-warn">The test is optional, but tap it once: until you do, your new leads go to head office.</div>;
  return (
    <Tabs tabs={[
      { key: 'ios', label: 'iPhone', body: (
        <>
          <div className="chats-gd-warn">Use Safari. Not Chrome, and not a link opened inside WhatsApp or Telegram.</div>
          <ol className="chats-gd-steps">
            <li><div>In <b>Safari</b>, go to <b>{address}</b><small>Don't sign in yet.</small></div></li>
            <li><div>Tap <b>•••</b> at the bottom right (or the Share square with the arrow), tap <b>Share</b>, scroll down, tap <b>Add to Home Screen</b>, then <b>Add</b></div></li>
            <li><div>Close Safari. Open <b>CMB Chats</b> from your home screen<small>It opens with no address bar.</small></div></li>
            <li><div>Sign in with your <b>Sentinel</b> email and password<small>Forgotten it? Tap Forgot password? under Sign in.</small></div></li>
            <li><div>Tap <b>Allow alerts</b>, then <b>Allow</b></div></li>
            <li><div>Optional, but do it once: tap <b>Send me a test alert</b>, lock your phone, and tap the alert when it arrives<small>"Done. This phone now gets your new leads."</small></div></li>
          </ol>
          {leads}
        </>
      ) },
      { key: 'android', label: 'Android', body: (
        <>
          <div className="chats-gd-warn">Use Chrome, not a link opened inside WhatsApp or Telegram.</div>
          <ol className="chats-gd-steps">
            <li><div>In <b>Chrome</b>, go to <b>{address}</b> and sign in</div></li>
            <li><div>Tap <b>Allow alerts</b>, then <b>Allow</b></div></li>
            <li><div>Optional, but do it once: tap <b>Send me a test alert</b> and tap the alert when it arrives</div></li>
            <li><div>Optional: tap <b>Install</b> to put CMB Chats on your home screen<small>Alerts work without it.</small></div></li>
          </ol>
          {leads}
        </>
      ) },
      { key: 'computer', label: 'Computer', body: (
        <ol className="chats-gd-steps">
          <li><div>Open <b>{address}</b> in Chrome or Edge and sign in</div></li>
          <li><div>Click <b>Turn on</b> in the green bar, then <b>Allow</b></div></li>
          <li><div>Optional: <b>You</b>, then <b>Install on this computer</b><small>It opens in its own window.</small></div></li>
          <li><div>Set up your phone too<small>Leads only reach you through your phone.</small></div></li>
        </ol>
      ) },
    ]} />
  );
}

/** /guide: the main guide. */
export function GuideScreen() {
  const address = useAddress();
  return (
    <Page title="CMB Chats guide">
      <Hero mark="CMB Chats guide" title="CMB Chats"
        intro="CleanMyBed's customer WhatsApp, team chats and assistant in one app. This guide explains how it works, the rules that apply and how to set it up.">
        <a className="chats-gd-btn chats-gd-btn--white" href="#setup">Set up</a>
        <a className="chats-gd-btn chats-gd-btn--ghost" href="#how">How it works</a>
      </Hero>

      <Section kicker="Overview" title="What CMB Chats does" lead="It looks and works like WhatsApp. Customers use their normal WhatsApp and need nothing new.">
        <div className="chats-gd-grid3">
          <Card icon="💬" tone="green" title="Leads are routed to you"><p>A new WhatsApp lead goes to the operator for that area, with an alert on your phone.</p></Card>
          <Card icon="✦" tone="leo" title="Leo covers for you"><p>Leo, our virtual assistant, answers your chats when you are busy and after hours, and passes them to you when a person is needed.</p></Card>
          <Card icon="📋" title="Customer records alongside"><p>Bookings, past services and invoices from Sentinel are shown next to the chat.</p></Card>
        </div>
        <div className="chats-gd-phones">
          <Shot name="chats" alt="The Chats list: the owner on each chat, Leo's chats shaded violet" caption="Your chats: the owner first, Leo's chats in violet" />
          <Shot name="chat" alt="An open chat with the owner and the on-duty switch at the top" caption="A chat: the owner, and who is on duty with the switch" />
          <Shot name="info" alt="Info with Leo off for this customer at the top" caption="Info: Leo off for this customer at the top" />
        </div>
      </Section>

      <Section id="how" alt kicker="How it works" title="How a chat is handled"
        lead="One switch decides who answers your chats. It sits at the top of Chats, Team and Ask: tap it to change. On a phone it is also in Settings, under My availability.">
        <div className="chats-gd-grid2">
          <div className="chats-gd-card"><span className="chats-gd-chip chats-gd-chip--avail">Available</span><h3>You answer</h3><p>Leo stands by. Reply within 10 minutes. Can't? Switch Leo on duty.</p></div>
          <div className="chats-gd-card"><span className="chats-gd-chip chats-gd-chip--duty">Leo on duty</span><h3>Leo answers</h3><p>Your phone stays quiet. You still get your leads. If he needs a person, you get an alert.</p></div>
        </div>
        <div className="chats-gd-grid3 chats-gd-gap">
          <Card icon="🌙" tone="amber" title="After hours"><p>Outside Monday to Friday, 08:00 to 17:00, and on public holidays, Leo is on duty automatically.</p></Card>
          <Card icon="💬" title="New chats"><p>A chat nobody owns yet is answered by Leo first. Head office is alerted if he needs a person, and can step in at any time.</p></Card>
          <Card icon="⏱️" tone="amber" title="Safety catch"><p>Available but no reply after 10 minutes? You are asked to reply or switch Leo on duty. At 20 minutes Leo covers that chat. Your next reply takes it back. Booking, moving or cancelling their booking counts as a reply.</p></Card>
        </div>
        <p className="chats-gd-note">Always answered by a person: Peace of Sleep chats, and chats about an invoice or payment, which go to Accounts.</p>
        <p className="chats-gd-note">A customer you want to handle yourself, always? At the top of the chat's Info, turn on <b>Leo off for this customer</b>. Leo never answers them, now or when they come back, and never covers at 20 minutes. Your reminders still come, so reply within 10 minutes.</p>
      </Section>

      <Section kicker="Getting started" title="Five things to know first" lead="These cover most of a normal day.">
        <ol className="chats-gd-quick">
          <li><div className="chats-gd-num">1</div><div><b>Look at Reply</b><span>The list opens on Reply: every chat there needs you. Aim to reply within 10 minutes during working hours.</span></div></li>
          <li><div className="chats-gd-num">2</div><div><b>Type and send</b><span>Your reply waits 5 seconds with Undo before it goes.</span></div></li>
          <li><div className="chats-gd-num">3</div><div><b>Tap Info for the customer's details</b><span>Top right of the chat: Customer, Booking and History.</span></div></li>
          <li><div className="chats-gd-num">4</div><div><b>Set your switch</b><span>At the top of Chats: Available when you can reply, Leo on duty when you can't.</span></div></li>
          <li><div className="chats-gd-num">5</div><div><b>Available means 10 minutes</b><span>Going into a service or away? Switch Leo on duty first.</span></div></li>
        </ol>
      </Section>

      <Section alt kicker="WhatsApp rules" title="Rules that apply to every business" lead="These are set by Meta, which runs WhatsApp. The app shows you when one applies.">
        <div className="chats-gd-grid3">
          <div className="chats-gd-card"><div className="chats-gd-big">24h</div><h3>Reply window</h3><p>After a customer writes, you can reply freely for 24 hours. After that, only a pre-approved message can be sent. Their next reply opens the window again.</p></div>
          <div className="chats-gd-card"><div className="chats-gd-big">Small fee</div><h3>Messages cost a little</h3><p>Meta charges CleanMyBed a small amount for each approved message we start a chat with, and for replies once we pass 1,000 in a month. Keep replies useful, not chatty.</p></div>
          <div className="chats-gd-card"><div className="chats-gd-big">CMB</div><h3>Sent from the business number</h3><p>Messages go from the CleanMyBed number (or Peace of Sleep for its chats), never your own. Your name is only shown if you add it.</p></div>
        </div>
        <p className="chats-gd-note">WhatsApp limits how many marketing messages one person gets (follow-ups, introductions, service due). If it holds one back, the chat says "WhatsApp held this back" and when to try again, and the app blocks a retry for 24 hours. Call or email them from Info meanwhile. Booking and payment messages always go.</p>
      </Section>

      <Section leo kicker="About Leo" title="How Leo is tested and improved"
        lead="Before going live, Leo was tested in practice chats with made-up customers, and his replies were checked by our team. Testing continues on real chats.">
        <div className="chats-gd-grid4">
          <div className="chats-gd-card chats-gd-stat"><div className="chats-gd-big chats-gd-big--leo">203</div><p>practice conversations</p></div>
          <div className="chats-gd-card chats-gd-stat"><div className="chats-gd-big chats-gd-big--leo">35</div><p>customer types tested</p></div>
          <div className="chats-gd-card chats-gd-stat"><div className="chats-gd-big chats-gd-big--leo">1,627</div><p>replies tested</p></div>
          <div className="chats-gd-card chats-gd-stat"><div className="chats-gd-big chats-gd-big--leo">366</div><p>replies rated by the team</p></div>
        </div>
        <div className="chats-gd-grid2 chats-gd-gap">
          <Card icon="👍" tone="leo" title="Rate his replies"><p>A thumbs up and thumbs down sit under each of Leo's replies. Tap one to say whether the reply was right. If it was wrong, add a short note. Head office reviews every rating and corrects Leo.</p></Card>
          <Card icon="🛑" tone="amber" title="What Leo passes to a person">
            <ul className="chats-gd-cross">
              <li>Complaints, and anyone asking for a person</li>
              <li>Changes and cancellations to a booking</li>
              <li>Discounts, businesses and guesthouses</li>
              <li>Payments and invoices, which go to Accounts</li>
            </ul>
          </Card>
        </div>
        <More title="How Leo works" text="Who answers when, the 10 and 20 minute safety catch, hand-overs, and what Leo will and will not say." to="/guide/leo" label="How Leo works" />
      </Section>

      <Section kicker="Replacing Telegram" title="Customer and team chats in one place"
        lead="CMB Chats replaces Telegram for lead alerts and team messages, so every lead and conversation stays on the CleanMyBed platform.">
        <div className="chats-gd-grid2">
          <div className="chats-gd-before"><h3>Before</h3><ul><li>Lead alerts on Telegram</li><li>Customers on personal WhatsApp</li><li>Team messages in several apps</li><li>Chat history lost when someone leaves</li></ul></div>
          <div className="chats-gd-after"><h3>Now</h3><ul><li>Lead alerts in CMB Chats</li><li>Customers on the CleanMyBed number</li><li>Team chats, groups and channels under Team</li><li>All chats kept by CleanMyBed</li></ul></div>
        </div>
      </Section>

      <Section alt kicker="Features" title="Features by screen" lead="Tap a screen to see what it does.">
        <div className="chats-gd-feats">
          {FEATURES.map((f) => (
            <details key={f.title} className="chats-gd-feat">
              <summary><span className={`chats-gd-icon${f.tone ? ` chats-gd-icon--${f.tone}` : ''}`} aria-hidden="true">{f.icon}</span><span>{f.title}<small>{f.sub}</small></span></summary>
              <ul>{f.items.map((item, i) => <li key={i}>{item}</li>)}</ul>
            </details>
          ))}
        </div>
      </Section>

      <section className="chats-gd-sec">
        <div className="chats-gd-wrap">
          <div className="chats-gd-card chats-gd-bug">
            <div className="chats-gd-icon chats-gd-icon--amber" aria-hidden="true">🐞</div>
            <div>
              <h3>Report a problem or suggest an idea</h3>
              <p>Tap the <b>bug</b> at the top of Chats, Team or Ask. Choose <b>Not working</b> or <b>Idea</b>, describe it, add a screenshot if useful, then tap <b>Send to head office</b>. Only head office sees reports. A wrong answer from Leo? Use the 👎 under his reply instead.</p>
            </div>
          </div>
        </div>
      </section>

      <Section id="setup" alt kicker="Set up" title="Set up in about five minutes" lead="Do it on your phone: that is where your leads arrive. Use your Sentinel email and password. The app walks you through the same steps.">
        <QuickSetup address={address} />
        <div className="chats-gd-open"><Link className="chats-gd-btn chats-gd-btn--violet" to="/">Open CMB Chats</Link></div>
        <More title="Need more detail?" text="The full set-up guide covers each step with screenshots, blocked alerts and phone settings." to="/guide/setup" label="Full set-up guide" />
      </Section>
    </Page>
  );
}

const SETUP_STEPS = ['Open it in the right place', 'iPhone: add to your home screen', 'Sign in', 'Allow alerts', 'Tap a test alert (optional)', 'Alert not showing?', 'Still stuck?'] as const;

function SetupStep({ n, children, shot }: { n: number; children: ReactNode; shot?: ReactNode }) {
  return (
    <section className="chats-gd-step" id={`step-${n}`}>
      <div className="chats-gd-step-head"><div className="chats-gd-num">{n}</div><h2>{SETUP_STEPS[n - 1]}</h2></div>
      <div className={`chats-gd-step-body${shot ? '' : ' chats-gd-step-body--solo'}`}>
        <div>{children}</div>
        {shot}
      </div>
    </section>
  );
}

/** /guide/setup: every set-up step in detail, matching the app's own set-up screens (19 Oct rebuild). */
export function GuideSetupScreen() {
  const address = useAddress();
  return (
    <Page title="Full set-up guide">
      <Hero back mark="Full set-up guide" title="Set up CMB Chats" intro="About five minutes. The app walks you through the same steps, in this order. Do them on your phone: that is where your leads arrive." />
      <div className="chats-gd-wrap chats-gd-layout">
        <nav className="chats-gd-toc" aria-label="Steps">
          <div className="chats-gd-kicker">Steps</div>
          <ol>{SETUP_STEPS.map((t, i) => <li key={t}><a href={`#step-${i + 1}`}>{t}</a></li>)}</ol>
        </nav>
        <div>
          <SetupStep n={1}>
            <p>Go to <b>{address}</b></p>
            <ul className="chats-gd-plain">
              <li><b>iPhone:</b> in <b>Safari</b>.</li>
              <li><b>Android:</b> in <b>Chrome</b>.</li>
              <li><b>Computer:</b> in <b>Chrome</b> or <b>Edge</b>.</li>
            </ul>
            <div className="chats-gd-warn">Opened the link inside WhatsApp, Telegram or iPhone Chrome? The app says so. Tap <b>Copy link</b> (if copying is blocked, press and hold the link to copy it), open Safari (or Chrome on Android), paste it in the address bar and go.</div>
            <p className="chats-gd-note">Don't use the Chats button inside Sentinel on your phone. It only shows a bar that opens CMB Chats on its own.</p>
          </SetupStep>

          <SetupStep n={2}>
            <p><b>iPhone only, and before you sign in.</b> On iPhone, alerts only work from the home screen, and a sign-in in Safari does not carry over.</p>
            <ol className="chats-gd-steps">
              <li><div>In Safari, tap <b>•••</b> at the bottom right, or the <b>Share</b> button (the square with the arrow)</div></li>
              <li><div>Tap <b>Share</b> if you see it, scroll down, and tap <b>Add to Home Screen</b><small>Leave Open as Web App on.</small></div></li>
              <li><div>Tap <b>Add</b></div></li>
              <li><div>Close Safari and open <b>CMB Chats</b> from your home screen<small>It opens with no address bar.</small></div></li>
            </ol>
            <p className="chats-gd-note">Opens with an address bar? Delete that icon and add it again from Safari. On Android this step is optional and comes at the end.</p>
          </SetupStep>

          <SetupStep n={3} shot={<Shot name="login" alt="The CMB Chats sign-in screen" caption="Sign in once" />}>
            <p>Sign in with your <b>Sentinel</b> email and password. It is the same login as Sentinel. There is no separate sign-up.</p>
            <ul className="chats-gd-plain">
              <li>Tap <b>Show password</b> to check what you typed.</li>
              <li>Forgotten it? Tap <b>Forgot password?</b>, set a new one from the email, then come back and sign in.</li>
              <li>Told the password is wrong? Tap <b>Email me a reset link</b>.</li>
            </ul>
          </SetupStep>

          <SetupStep n={4}>
            <p>Without alerts, a new lead can sit unseen.</p>
            <ol className="chats-gd-steps">
              <li><div>Tap <b>Allow alerts</b></div></li>
              <li><div>When your phone asks, tap <b>Allow</b></div></li>
            </ol>
            <p className="chats-gd-sub">Tapped Block by mistake?</p>
            <ul className="chats-gd-plain">
              <li><b>iPhone</b>: Settings, Notifications, <b>CMB Chats</b>. Turn on <b>Allow Notifications</b>.</li>
              <li><b>Android</b>: Settings, Apps, <b>CMB Chats</b> (or Chrome), Notifications. Allow them.</li>
              <li><b>Computer</b>: click the padlock beside the web address. Set Notifications to <b>Allow</b>, then reload.</li>
            </ul>
          </SetupStep>

          <SetupStep n={5} shot={<Shot name="you" alt="Settings with the test alert button" caption="The test alert" />}>
            <ol className="chats-gd-steps">
              <li><div>Tap <b>Send me a test alert</b></div></li>
              <li><div>Lock your phone straight away</div></li>
              <li><div>When the alert arrives, tap it</div></li>
            </ol>
            <div className="chats-gd-ok">The test is optional, but tap it once: until you do, your new leads go to head office. Once tapped: "Done. This phone now gets your new leads."</div>
            <p className="chats-gd-note">Skipped it? <b>Go to my chats</b> works once alerts are allowed. Do the test later under <b>You</b>, then <b>Send me a test notification</b>. Sharing a phone? Each person taps their own test after signing in.</p>
          </SetupStep>

          <SetupStep n={6}>
            <p>The app asks <b>Did you see it?</b> Tap <b>No</b> and it shows the fix for your phone. These are the usual causes:</p>
            <div className="chats-gd-gap chats-gd-fixes">
              <Tabs tabs={[
                { key: 'ios', label: 'iPhone', body: <PhoneSettingsHelp os="ios" /> },
                { key: 'android', label: 'Android', body: <PhoneSettingsHelp os="android" /> },
                { key: 'mac', label: 'Mac', body: <ComputerAlertsHelp os="mac" browser="Chrome" /> },
                { key: 'windows', label: 'Windows', body: <ComputerAlertsHelp os="windows" browser="Chrome" /> },
              ]} />
            </div>
            <p className="chats-gd-note">"Sentinel could not reach this phone"? Close CMB Chats fully, open it again from your home screen, then send another test.</p>
          </SetupStep>

          <SetupStep n={7}>
            <p>Once you are signed in, message head office on the <b>Team</b> tab, or tap the <b>bug</b> at the top of Chats and choose <b>Not working</b>.</p>
            <p className="chats-gd-note">Can't sign in at all? Contact head office the way you normally do.</p>
            <div className="chats-gd-open">
              <Link className="chats-gd-btn chats-gd-btn--violet" to="/">Open CMB Chats</Link>
              <GuideLink className="chats-gd-btn chats-gd-btn--line" to="/guide">Back to the guide</GuideLink>
            </div>
          </SetupStep>
        </div>
      </div>
    </Page>
  );
}

function Moment({ who, tone, title, text }: { who: string; tone: 'customer' | 'leo' | 'you' | 'app'; title: string; text: string }) {
  return <li><div className={`chats-gd-who chats-gd-who--${tone}`}>{who}</div><div><b>{title}</b><span>{text}</span></div></li>;
}

/** /guide/leo: how Leo and the operator share a chat, in plain words. */
export function GuideLeoScreen() {
  return (
    <Page title="How Leo works">
      <Hero back mark="How Leo works" title="Leo, our virtual assistant" intro="Who answers a customer, when Leo steps in, when he hands over to you, and what he will and will not say." />

      <section className="chats-gd-sec">
        <div className="chats-gd-wrap">
          <div className="chats-gd-rule">
            <div className="chats-gd-kicker">The one rule</div>
            <p>When you can reply, you answer. When you can't, Leo answers. Your reply always takes the chat back.</p>
          </div>
        </div>
      </section>

      <Section kicker="Your switch" title="You choose who answers your chats"
        lead="The switch sits at the top of Chats, Team and Ask. Tap it to change. On a phone it is also in Settings, under My availability.">
        <div className="chats-gd-grid2">
          <div className="chats-gd-card"><span className="chats-gd-chip chats-gd-chip--avail">Available</span><h3>You answer</h3><p>Leo stands by. Reply to customers within 10 minutes.</p></div>
          <div className="chats-gd-card"><span className="chats-gd-chip chats-gd-chip--duty">Leo on duty</span><h3>Leo answers</h3><p>Your phone stays quiet for chats Leo is handling. New leads still come to you. You are alerted if he needs you.</p></div>
        </div>
        <div className="chats-gd-card chats-gd-gap">
          <h3>Simple habit</h3>
          <p>Going into a service, a meeting or away? Switch <b>Leo on duty</b> first. Back? Switch to <b>Available</b>. Reply while you are on Leo on duty and the chat asks "You're replying. Switch to Available?"</p>
          <p>Just one chat to hand over? Flip the <b>switch</b> on the chat's top line to Leo. He picks it up, answers at once if the customer is waiting, and your reminders stop.</p>
          <p>A customer you always answer yourself? At the top of Info, turn on <b>Leo off for this customer</b>. Leo stays out of every chat with them, after hours and at 20 minutes included, until you turn it off. Their chats show "Leo off" in the list.</p>
        </div>
      </Section>

      <Section alt kicker="Automatic" title="When Leo answers without the switch" lead="These happen for everyone, whatever your switch says.">
        <div className="chats-gd-grid3">
          <Card icon="🌙" tone="amber" title="After hours"><p>Outside Monday to Friday, 08:00 to 17:00, and on public holidays.</p></Card>
          <Card icon="💬" title="A brand new chat"><p>Nobody owns it yet, so Leo answers first. Head office can step in at any time.</p></Card>
          <Card icon="⏱️" tone="amber" title="A customer left waiting"><p>You are Available but have not replied for 20 minutes. Leo covers that chat.</p></Card>
        </div>
      </Section>

      <Section kicker="Step by step" title="A new enquiry" lead="What happens when someone messages us for the first time.">
        <div className="chats-gd-card">
          <ol className="chats-gd-time">
            <Moment who="Customer" tone="customer" title="Messages our WhatsApp number" text="From an ad, the website or a landing page." />
            <Moment who="Leo" tone="leo" title="Replies within seconds" text="Prices from our price list, how the service works, open times." />
            <Moment who="Leo" tone="leo" title="Asks for their suburb early" text="The suburb decides which operator gets the lead. A town we don't serve yet goes on our wait list, with no booking link." />
            <Moment who="Sentinel" tone="app" title="The lead goes to the area's operator" text="Your phone is alerted. You can read along at any time." />
            <Moment who="Leo" tone="leo" title="Grades the enquiry" text="Every chat starts Warm. Hot means ready to book or a big job, and your phone gets a Hot lead alert. Cold gets kind, short replies and no chasing. A Warm chat quiet for 7 days turns Cold overnight." />
            <Moment who="You" tone="you" title="Reply when you are ready" text="Leo goes quiet straight away. The chat is yours." />
          </ol>
        </div>
        <h3 className="chats-gd-h3gap">A website form</h3>
        <div className="chats-gd-card">
          <ol className="chats-gd-time">
            <Moment who="Customer" tone="customer" title="Sends a form on our website" text="With a mobile number." />
            <Moment who="Sentinel" tone="app" title="A WhatsApp welcome goes out" text="With three buttons: Get a price, Book a time and Call me. The chat opens with the lead's details, assigned to the area's operator. Nothing is owed until the customer replies." />
            <Moment who="No welcome?" tone="app" title="The chat says why" text="For example, the number asked not to be messaged, or was welcomed in the last week. Phone or email them." />
            <Moment who="Leo" tone="leo" title="Answers their reply first" text="Until you write in the chat." />
            <Moment who="Call me" tone="leo" title="Leo hands it to you" text="He tells the customer the call is noted, and you get an alert. Phone them." />
            <Moment who="You" tone="you" title="Email only, no mobile?" text="Nothing can go on WhatsApp. Email or call them." />
          </ol>
        </div>
      </Section>

      <Section alt kicker="When you are Available" title="A customer writes and you are busy" lead="The safety catch, so no customer waits in silence.">
        <div className="chats-gd-card">
          <ol className="chats-gd-time">
            <Moment who="0 min" tone="customer" title="The customer writes" text="Your phone is alerted." />
            <Moment who="10 min" tone="app" title="&quot;Still available?&quot;" text="Reply now, or switch Leo on duty." />
            <Moment who="20 min" tone="leo" title="Leo covers the chat" text="He tells the customer you will reply shortly, and answers what he can. The chat shows Leo is covering." />
            <Moment who="Later" tone="you" title="Your next reply takes the chat back" text="Leo stops at once." />
          </ol>
          <p className="chats-gd-note">Booking, moving or cancelling their booking counts as a reply, so the timer stops. Take over or reassign a chat and the 10 and 20 minutes start again for the new owner.</p>
        </div>
      </Section>

      <Section kicker="Handing over" title="When Leo needs a person" lead="Leo answers what he can first, then tells the customer the team will reply.">
        <div className="chats-gd-grid2">
          <Card icon="🤝" tone="amber" title="Leo passes these to you">
            <ul className="chats-gd-plain">
              <li>Complaints</li>
              <li>Anyone asking for a person</li>
              <li>Changing or cancelling a booking</li>
              <li>Discounts</li>
              <li>Businesses, guesthouses and licensing</li>
              <li>Holding a specific time</li>
            </ul>
          </Card>
          <Card icon="🔔" tone="green" title="What you do">
            <ul className="chats-gd-plain">
              <li>Your phone is alerted, and the chat shows <b>why</b> Leo handed over.</li>
              <li>Reply to the customer. The chat is yours.</li>
              <li>Until you reply, Leo keeps the customer company and notes anything new for you. Each new customer message alerts you too.</li>
              <li>Nobody replies? Head office is flagged.</li>
            </ul>
          </Card>
        </div>
      </Section>

      <Section alt kicker="The boundaries" title="What Leo does, and never does">
        <div className="chats-gd-grid3">
          <div className="chats-gd-card">
            <h3>Leo does</h3>
            <ul className="chats-gd-tick">
              <li>Quotes from our price list, as an estimate the operator confirms on site</li>
              <li>Sends our price list picture</li>
              <li>Checks open times and points to the booking page</li>
              <li>Notes visit details for you, such as gate codes, pets or an extra bed</li>
              <li>Answers existing customers from their history</li>
            </ul>
          </div>
          <div className="chats-gd-card">
            <h3>Leo also</h3>
            <ul className="chats-gd-tick">
              <li>Marks job seekers, people who want to buy our machine or start their own business, and people selling to us as Junk himself, pointing the first two to our opportunities page, and cancels an unconfirmed website booking they made. The chat closes with a note; if he got it wrong, reopen the chat from Info</li>
              <li>Knows when a booking was cancelled in the last 30 days, and says so</li>
              <li>Gives no price in a chat once anyone on our team has talked price there (an amount, a rate, a quote, a special or a discount): he notes the question and says you will confirm the price</li>
              <li>Stays out of a chat for 30 minutes after anyone on our team writes in it, even when you are set to Leo on duty</li>
              <li>Stays quiet on a bare "thanks", "makes sense" or "got it", so nothing is owed</li>
              <li>When you switch him back on, answers every message the customer sent since his last reply</li>
              <li>Says he can't listen to a voice note or see a photo, passes it to you, and asks them to type it if they can</li>
            </ul>
          </div>
          <div className="chats-gd-card">
            <h3>Leo never</h3>
            <ul className="chats-gd-cross">
              <li>Offers anything not on our price list, such as duvets, bedding, carpets, rugs or curtains</li>
              <li>Says an operator takes cards on site</li>
              <li>Books, holds or promises a time</li>
              <li>Gives bank details, amounts owed or payment links</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section kicker="Always a person" title="Chats Leo stays out of">
        <div className="chats-gd-grid3">
          <Card icon="💳" tone="amber" title="Money"><p>A chat about an invoice or a payment goes to Accounts. Leo stays out.</p></Card>
          <Card icon="🏨" title="Peace of Sleep"><p>Hospitality chats are always answered by our team.</p></Card>
          <Card icon="✋" tone="green" title="STOP"><p>A customer who sends STOP never hears from Leo again.</p></Card>
        </div>
        <div className="chats-gd-grid3 chats-gd-gap">
          <Card icon="✉️" title="Email"><p>Leo never sends an email. He suggests a reply (Leo's draft) for a person to check and send, and closes junk sent to info@.</p></Card>
          <Card icon="🚫" tone="amber" title="Suburbs we don't service"><p>Leo says once, kindly, that we don't have a team member there at the moment: no times, no booking, no lead alert. The lead is closed as Out of our area. The booking page says the same.</p></Card>
          <Card icon="🤝" title="Suburbs by arrangement"><p>Leo gives no times. He takes the beds and preferred days and hands to you to confirm and decide any travel charge. On the booking page it becomes a request: you get "Booking request (by arrangement)" to confirm.</p></Card>
        </div>
        <p className="chats-gd-note">Head office sets each suburb's service in Sentinel. Travel charge questions go to you, never Accounts, and Leo never names an amount.</p>
        <p className="chats-gd-note">If Leo cannot answer (a daily limit, a monthly budget or a fault), he sends one honest message, you and head office are alerted, and he stays off that chat until we next open.</p>
      </Section>

      <Section leo kicker="Help Leo learn" title="Rate his replies">
        <div className="chats-gd-grid2">
          <div className="chats-gd-card">
            <p>Under each of Leo's replies there is a 👍 and a 👎. Tap one. If he got it wrong, say what was wrong in a few words.</p>
            <p>Head office reads every rating and corrects Leo, often the same day.</p>
          </div>
          <Shot name="chat" alt="The top of a chat: the owner and the on-duty switch" caption="The top of every chat: the owner and who is on duty" />
        </div>
      </Section>

      <Section alt kicker="Who can do what" title="Your controls">
        <div className="chats-gd-grid2">
          <div className="chats-gd-card">
            <h3>Operators</h3>
            <ul className="chats-gd-plain">
              <li><b>Reply</b> or flip the <b>switch</b> on the top line to take a chat from Leo</li>
              <li>Flip the <b>switch</b> back to hand one of your chats to Leo</li>
              <li><b>Leo off for this customer</b>, at the top of Info, for customers you always answer yourself</li>
              <li><b>Available</b> or <b>Leo on duty</b> switch</li>
              <li><b>Rate</b> Leo's replies</li>
            </ul>
          </div>
          <div className="chats-gd-card">
            <h3>Head office</h3>
            <ul className="chats-gd-plain">
              <li>All of that, on any chat</li>
              <li>Write in a chat that is someone else's: a team note, Tell Leo, or take it over</li>
              <li><b>Change</b> the owner, in Info</li>
            </ul>
          </div>
        </div>
        <div className="chats-gd-open"><GuideLink className="chats-gd-btn chats-gd-btn--line" to="/guide">Back to the guide</GuideLink></div>
      </Section>
    </Page>
  );
}
